import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FileText, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, StatusBadge, Loading, EmptyState } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { money, fmtDay } from "@/lib/format";

const FILTERS = {
  estimate: ["all", "draft", "sent", "approved", "declined", "converted"],
  invoice: ["all", "draft", "unpaid", "partially_paid", "overdue", "paid", "void"],
};

export default function DocList({ kind }) {
  const isEst = kind === "estimate";
  const base = isEst ? "/estimates" : "/invoices";
  const [filter, setFilter] = useState("all");
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setDocs(null);
    api.get(base, { params: filter === "all" ? {} : { status: filter } }).then((r) => setDocs(r.data)).catch((e) => setError(errMsg(e)));
  }, [base, filter]);

  useEffect(() => {
    setFilter("all");
  }, [kind]);

  const Icon = isEst ? FileText : Receipt;
  return (
    <div data-testid={`${kind}-list-page`}>
      <PageHeader
        title={isEst ? "Estimates" : "Invoices"}
        subtitle={isEst ? "Quotes your clients can approve online." : "Track what's billed, paid and overdue."}
        actions={<Button asChild className="h-11"><Link to={`${base}/new`} data-testid={`new-${kind}-btn`}><Plus className="h-4 w-4 mr-1" />New {kind}</Link></Button>}
      />
      <Tabs value={filter} onValueChange={setFilter} className="mb-5 overflow-x-auto">
        <TabsList className="h-11">
          {FILTERS[kind].map((f) => <TabsTrigger key={f} value={f} className="h-9 capitalize" data-testid={`${kind}-filter-${f}`}>{f.replace("_", " ")}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {error && <p className="text-sm text-red-700">{error}</p>}
      {!docs ? <Loading /> : docs.length === 0 ? (
        <EmptyState icon={Icon} title={`No ${kind}s here`} text={isEst ? "Create an estimate or use the price calculator." : "Create an invoice or convert an approved estimate."} testId={`${kind}-empty`} />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid={`${kind}-list`}>
          {docs.map((d) => (
            <Link key={d.id} to={`${base}/${d.id}`} data-testid={`${kind}-row-${d.id}`} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{d.client_name}</div>
                <div className="text-xs text-slate-500">{d.number} · {isEst ? `Created ${fmtDay(d.created_at)}` : `Due ${fmtDay(d.due_date)}`}</div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-slate-900">{money(isEst ? d.total : d.payment_status === "paid" ? d.total : d.balance)}</div>
                {!isEst && d.payment_status !== "paid" && d.payment_status !== "void" && <div className="text-[11px] text-slate-500">of {money(d.total)}</div>}
              </div>
              <StatusBadge status={isEst ? d.status : d.payment_status} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
